import { useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { templates as api, type EmailBlock, PERSONALIZATION_TOKENS } from "@/lib/api";
import {
  Type, AlignLeft, Image, MousePointerClick, Minus, ArrowUpDown,
  Trash2, GripVertical, ChevronUp, ChevronDown, Eye,
  Save, ArrowLeft, Sparkles, Copy, Check, Shuffle,
} from "lucide-react";
import clsx from "clsx";
import { SITE_URL } from "@/lib/siteConfig";

// ── Block type definitions ──
const BLOCK_TYPES = [
  { type: "heading", icon: Type, label: "Heading" },
  { type: "text", icon: AlignLeft, label: "Text" },
  { type: "image", icon: Image, label: "Image" },
  { type: "button", icon: MousePointerClick, label: "Button" },
  { type: "divider", icon: Minus, label: "Divider" },
  { type: "spacer", icon: ArrowUpDown, label: "Spacer" },
] as const;

function createBlock(type: string): EmailBlock {
  const id = `blk_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  switch (type) {
    case "heading": return { type: "heading", id, content: "Your headline here", level: "h1", align: "center", color: "#0B1215" };
    case "text": return { type: "text", id, content: "Write your email content here. Keep it concise and compelling.", align: "left", color: "#333333", fontSize: 16 };
    case "image": return { type: "image", id, src: "https://placehold.co/600x200/FF5F45/FFFFFF?text=Your+Image", alt: "Image", width: "100%", align: "center" };
    case "button": return { type: "button", id, text: "Click Here", href: SITE_URL, bgColor: "#FF5F45", color: "#FFFFFF", align: "center", borderRadius: 6 };
    case "divider": return { type: "divider", id, color: "#E0E0E0", thickness: 1 };
    case "spacer": return { type: "spacer", id, height: 24 };
    default: return { type: "text", id, content: "" };
  }
}

// ── Block Preview (canvas renderer) ──
function BlockPreview({ block }: { block: EmailBlock }) {
  switch (block.type) {
    case "heading": {
      const sizes = { h1: "text-2xl", h2: "text-xl", h3: "text-lg" };
      return (
        <div style={{ textAlign: block.align || "center", color: block.color || "#0B1215" }}
          className={`font-bold ${sizes[block.level as keyof typeof sizes] || "text-2xl"}`}>
          {block.content || "Heading"}
        </div>
      );
    }
    case "text":
      return (
        <p style={{ textAlign: block.align || "left", color: block.color || "#333", fontSize: block.fontSize || 16 }}
          className="leading-relaxed m-0">{block.content || "Text block"}</p>
      );
    case "image":
      return (
        <div style={{ textAlign: block.align || "center" }}>
          <img src={block.src} alt={block.alt || ""} style={{ maxWidth: "100%", width: block.width || "100%", height: "auto", display: "inline-block", borderRadius: 4 }} />
        </div>
      );
    case "button":
      return (
        <div style={{ textAlign: block.align || "center" }}>
          <span style={{
            display: "inline-block", padding: "12px 28px",
            background: block.bgColor || "#FF5F45", color: block.color || "#FFF",
            borderRadius: block.borderRadius ?? 6, fontWeight: 600, fontSize: 16,
          }}>{block.text || "Button"}</span>
        </div>
      );
    case "divider":
      return <hr style={{ border: "none", borderTop: `${block.thickness || 1}px solid ${block.color || "#E0E0E0"}`, margin: "8px 0" }} />;
    case "spacer":
      return <div style={{ height: block.height || 24 }} className="bg-gray-50/50 border border-dashed border-gray-200 rounded flex items-center justify-center text-[10px] text-gray-400">{block.height || 24}px</div>;
    default:
      return <div className="text-gray-400 text-sm">Unknown block</div>;
  }
}

// ── Properties Panel ──
function PropertiesPanel({ block, onChange }: { block: EmailBlock; onChange: (updated: EmailBlock) => void }) {
  const update = (key: string, value: any) => onChange({ ...block, [key]: value });

  return (
    <div className="space-y-4">
      <div className="text-xs font-bold uppercase tracking-wider text-slate mb-2">
        {block.type} properties
      </div>

      {/* Content */}
      {(block.type === "heading" || block.type === "text") && (
        <div>
          <label className="block text-xs font-semibold text-slate mb-1">Content</label>
          {block.type === "text" ? (
            <textarea value={block.content || ""} onChange={(e) => update("content", e.target.value)} rows={4}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          ) : (
            <input value={block.content || ""} onChange={(e) => update("content", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          )}
        </div>
      )}

      {/* Heading level */}
      {block.type === "heading" && (
        <div>
          <label className="block text-xs font-semibold text-slate mb-1">Level</label>
          <select value={block.level || "h1"} onChange={(e) => update("level", e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm">
            <option value="h1">H1 — Large</option>
            <option value="h2">H2 — Medium</option>
            <option value="h3">H3 — Small</option>
          </select>
        </div>
      )}

      {/* Font size for text */}
      {block.type === "text" && (
        <div>
          <label className="block text-xs font-semibold text-slate mb-1">Font Size: {block.fontSize || 16}px</label>
          <input type="range" min={12} max={32} value={block.fontSize || 16} onChange={(e) => update("fontSize", parseInt(e.target.value))}
            className="w-full" />
        </div>
      )}

      {/* Image props */}
      {block.type === "image" && (
        <>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Image URL</label>
            <input value={block.src || ""} onChange={(e) => update("src", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
              placeholder="https://..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Alt Text</label>
            <input value={block.alt || ""} onChange={(e) => update("alt", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Link URL (optional)</label>
            <input value={block.href || ""} onChange={(e) => update("href", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30"
              placeholder="https://..." />
          </div>
        </>
      )}

      {/* Button props */}
      {block.type === "button" && (
        <>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Button Text</label>
            <input value={block.text || ""} onChange={(e) => update("text", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Link URL</label>
            <input value={block.href || ""} onChange={(e) => update("href", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate mb-1">BG Color</label>
              <input type="color" value={block.bgColor || "#FF5F45"} onChange={(e) => update("bgColor", e.target.value)}
                className="w-full h-9 rounded-lg border border-black/[0.08] cursor-pointer" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate mb-1">Text Color</label>
              <input type="color" value={block.color || "#FFFFFF"} onChange={(e) => update("color", e.target.value)}
                className="w-full h-9 rounded-lg border border-black/[0.08] cursor-pointer" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Border Radius: {block.borderRadius ?? 6}px</label>
            <input type="range" min={0} max={32} value={block.borderRadius ?? 6} onChange={(e) => update("borderRadius", parseInt(e.target.value))}
              className="w-full" />
          </div>
        </>
      )}

      {/* Divider props */}
      {block.type === "divider" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Color</label>
            <input type="color" value={block.color || "#E0E0E0"} onChange={(e) => update("color", e.target.value)}
              className="w-full h-9 rounded-lg border border-black/[0.08] cursor-pointer" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate mb-1">Thickness</label>
            <input type="number" min={1} max={8} value={block.thickness || 1} onChange={(e) => update("thickness", parseInt(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-black/[0.08] bg-paper text-sm" />
          </div>
        </div>
      )}

      {/* Spacer props */}
      {block.type === "spacer" && (
        <div>
          <label className="block text-xs font-semibold text-slate mb-1">Height: {block.height || 24}px</label>
          <input type="range" min={8} max={120} value={block.height || 24} onChange={(e) => update("height", parseInt(e.target.value))}
            className="w-full" />
        </div>
      )}

      {/* Alignment — shared */}
      {["heading", "text", "image", "button"].includes(block.type) && (
        <div>
          <label className="block text-xs font-semibold text-slate mb-1">Alignment</label>
          <div className="flex gap-1">
            {["left", "center", "right"].map((a) => (
              <button key={a} onClick={() => update("align", a)}
                className={clsx("flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors",
                  block.align === a ? "bg-ink text-white" : "bg-paper text-slate hover:bg-black/[0.04]"
                )}>{a}</button>
            ))}
          </div>
        </div>
      )}

      {/* Text color — shared */}
      {["heading", "text"].includes(block.type) && (
        <div>
          <label className="block text-xs font-semibold text-slate mb-1">Color</label>
          <input type="color" value={block.color || "#333333"} onChange={(e) => update("color", e.target.value)}
            className="w-full h-9 rounded-lg border border-black/[0.08] cursor-pointer" />
        </div>
      )}
    </div>
  );
}

// ── Token Picker ──
// Shows all personalisation tokens with copy-to-clipboard. Lives in the bottom
// of the right panel so it's always accessible without scrolling away from props.
function TokenPicker() {
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  const copy = (token: string) => {
    navigator.clipboard.writeText(token).catch(() => {});
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
  };

  const SPINTAX_EXAMPLES = [
    { label: "Greeting", example: "{Hi|Hello|Hey} {{first_name}}" },
    { label: "CTA",      example: "{Book a call|Schedule time|Grab 15 mins}" },
    { label: "Opener",   example: "{Quick question|Just wondering|Curious}" },
  ];

  return (
    <div className="border-t border-black/[0.06] p-4">
      {/* Personalisation tokens */}
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkles size={13} className="text-coral" />
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate">Personalisation Tokens</p>
      </div>
      <div className="space-y-1">
        {PERSONALIZATION_TOKENS.map(({ token, label, description }) => (
          <button
            key={token}
            onClick={() => copy(token)}
            title={description}
            className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg hover:bg-black/[0.03] transition-colors group text-left"
          >
            <div>
              <span className="text-xs font-mono text-coral">{token}</span>
              <span className="text-[11px] text-slate ml-2">{label}</span>
            </div>
            {copiedToken === token
              ? <Check size={12} className="text-green-500 shrink-0" />
              : <Copy size={11} className="text-slate opacity-0 group-hover:opacity-60 shrink-0" />
            }
          </button>
        ))}
      </div>
      <p className="text-[10px] text-slate/60 mt-2 leading-relaxed">
        Click to copy. Paste into any text or heading block. Tokens are replaced at send time.
      </p>

      {/* Spintax section */}
      <div className="mt-4 pt-3 border-t border-black/[0.06]">
        <div className="flex items-center gap-1.5 mb-2">
          <Shuffle size={13} className="text-teal" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate">Spintax</p>
        </div>
        <p className="text-[10px] text-slate/70 mb-2 leading-relaxed">
          Randomise words per recipient to avoid spam filters.
          Syntax: <span className="font-mono text-teal">{"{A|B|C}"}</span>
        </p>
        <div className="space-y-1">
          {SPINTAX_EXAMPLES.map(({ label, example }) => (
            <button
              key={label}
              onClick={() => copy(example)}
              title={`Copy ${label} spintax example`}
              className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg hover:bg-black/[0.03] transition-colors group text-left"
            >
              <div className="min-w-0">
                <span className="text-[11px] font-mono text-teal truncate block">{example}</span>
                <span className="text-[10px] text-slate">{label}</span>
              </div>
              {copiedToken === example
                ? <Check size={12} className="text-green-500 shrink-0 ml-1" />
                : <Copy size={11} className="text-slate opacity-0 group-hover:opacity-60 shrink-0 ml-1" />
              }
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate/60 mt-1.5 leading-relaxed">
          Nesting supported: <span className="font-mono">{"{Hi|Hey {there|{name}}}"}</span>
        </p>
      </div>
    </div>
  );
}

// ── Main Editor Page ──
export default function TemplateEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("campaign");

  const [blocks, setBlocks] = useState<EmailBlock[]>([
    createBlock("heading"),
    createBlock("text"),
    createBlock("button"),
  ]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState("Untitled Template");
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItem = useRef<{ type: "palette" | "canvas"; index?: number; blockType?: string } | null>(null);

  const selectedBlock = blocks.find((b) => b.id === selectedId) || null;

  // ── Block operations ──
  const updateBlock = useCallback((updated: EmailBlock) => {
    setBlocks((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  }, []);

  const removeBlock = useCallback((id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  }, [selectedId]);

  const moveBlock = useCallback((fromIndex: number, toIndex: number) => {
    setBlocks((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  // ── Drag & Drop ──
  const handleDragStart = (type: "palette" | "canvas", blockType?: string, index?: number) => {
    dragItem.current = { type, blockType, index };
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (!dragItem.current) return;

    if (dragItem.current.type === "palette" && dragItem.current.blockType) {
      const newBlock = createBlock(dragItem.current.blockType);
      setBlocks((prev) => {
        const next = [...prev];
        next.splice(dropIndex, 0, newBlock);
        return next;
      });
      setSelectedId(newBlock.id);
    } else if (dragItem.current.type === "canvas" && dragItem.current.index !== undefined) {
      moveBlock(dragItem.current.index, dropIndex);
    }
    dragItem.current = null;
  };

  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (!dragItem.current) return;
    if (dragItem.current.type === "palette" && dragItem.current.blockType) {
      const newBlock = createBlock(dragItem.current.blockType);
      setBlocks((prev) => [...prev, newBlock]);
      setSelectedId(newBlock.id);
    }
    dragItem.current = null;
  };

  // ── Save ──
  const handleSave = async () => {
    setSaving(true);
    try {
      let savedTemplate;
      if (templateId) {
        // Update existing
        const result = await api.update(templateId, { name: templateName, blocks });
        savedTemplate = result.template;
      } else {
        // Create new
        const result = await api.create({ name: templateName, blocks });
        savedTemplate = result.template;
        setTemplateId(savedTemplate.id);
      }

      // If opened from a campaign, update the campaign's HTML content
      if (campaignId) {
        const rendered = await api.render(blocks);
        const { campaigns: campApi } = await import("@/lib/api");
        await campApi.update(campaignId, { html_content: rendered.html });
      }

      alert("Template saved!");
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  // ── Preview ──
  const handlePreview = async () => {
    try {
      const result = await api.render(blocks);
      setPreviewHtml(result.html);
    } catch (err) {
      console.error("Preview error:", err);
    }
  };

  return (
    <div className="h-screen flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-black/[0.06] bg-white shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/campaigns")} className="p-1.5 rounded-lg text-slate hover:bg-black/[0.04]">
            <ArrowLeft size={18} />
          </button>
          <input value={templateName} onChange={(e) => setTemplateName(e.target.value)}
            className="text-lg font-bold bg-transparent border-none outline-none focus:ring-0 w-64" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handlePreview}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-black/[0.08] hover:border-black/20">
            <Eye size={15} /> Preview
          </button>
          <button onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-60 shadow-md shadow-coral/20">
            <Save size={15} /> {saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Block Palette (left) */}
        <div className="w-52 border-r border-black/[0.06] bg-white p-4 shrink-0 overflow-y-auto">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate mb-3">Blocks</div>
          <div className="space-y-2">
            {BLOCK_TYPES.map((bt) => (
              <div key={bt.type}
                draggable
                onDragStart={() => handleDragStart("palette", bt.type)}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg border border-black/[0.06] bg-paper cursor-grab hover:border-coral/30 hover:bg-coral-light/30 transition-colors text-sm font-medium select-none">
                <bt.icon size={16} className="text-slate shrink-0" />
                {bt.label}
              </div>
            ))}
          </div>
          <div className="text-[10px] text-slate mt-4 leading-relaxed">
            Drag blocks onto the canvas to build your email template.
          </div>
        </div>

        {/* Canvas (center) */}
        <div className="flex-1 bg-gray-100 overflow-y-auto p-8"
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={handleCanvasDrop}>

          <div className="max-w-[640px] mx-auto">
            {/* Email container */}
            <div className="bg-white rounded-lg shadow-sm border border-black/[0.06] min-h-[400px]">
              {blocks.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-slate">
                  <AlignLeft size={32} className="mb-3 opacity-40" />
                  <p className="text-sm font-medium">Drag blocks here to start building</p>
                </div>
              )}

              {blocks.map((block, index) => (
                <div key={block.id}>
                  {/* Drop zone between blocks */}
                  <div
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    className={clsx(
                      "h-1 transition-all",
                      dragOverIndex === index ? "h-8 bg-coral/10 border-2 border-dashed border-coral/30 rounded mx-4" : ""
                    )}
                  />

                  {/* Block */}
                  <div
                    draggable
                    onDragStart={() => handleDragStart("canvas", undefined, index)}
                    onClick={() => setSelectedId(block.id)}
                    className={clsx(
                      "group relative px-6 py-3 cursor-pointer transition-all",
                      selectedId === block.id
                        ? "ring-2 ring-coral/40 ring-inset bg-coral/[0.02]"
                        : "hover:bg-black/[0.01]"
                    )}
                  >
                    {/* Block controls */}
                    <div className={clsx(
                      "absolute -left-0.5 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 transition-opacity",
                      selectedId === block.id ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                    )}>
                      <GripVertical size={14} className="text-slate cursor-grab" />
                    </div>

                    <div className={clsx(
                      "absolute right-2 top-2 flex items-center gap-1 transition-opacity",
                      selectedId === block.id ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                    )}>
                      {index > 0 && (
                        <button onClick={(e) => { e.stopPropagation(); moveBlock(index, index - 1); }}
                          className="p-1 rounded hover:bg-black/[0.06]"><ChevronUp size={13} /></button>
                      )}
                      {index < blocks.length - 1 && (
                        <button onClick={(e) => { e.stopPropagation(); moveBlock(index, index + 1); }}
                          className="p-1 rounded hover:bg-black/[0.06]"><ChevronDown size={13} /></button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); removeBlock(block.id); }}
                        className="p-1 rounded hover:bg-red-50 text-red-400"><Trash2 size={13} /></button>
                    </div>

                    <BlockPreview block={block} />
                  </div>
                </div>
              ))}

              {/* Final drop zone */}
              {blocks.length > 0 && (
                <div
                  onDragOver={(e) => handleDragOver(e, blocks.length)}
                  onDrop={(e) => handleDrop(e, blocks.length)}
                  className={clsx(
                    "h-4 transition-all",
                    dragOverIndex === blocks.length ? "h-8 bg-coral/10 border-2 border-dashed border-coral/30 rounded mx-4 mb-2" : ""
                  )}
                />
              )}
            </div>
          </div>
        </div>

        {/* Properties Panel (right) */}
        <div className="w-72 border-l border-black/[0.06] bg-white shrink-0 overflow-y-auto flex flex-col">
          <div className="p-5 flex-1">
            {selectedBlock ? (
              <PropertiesPanel block={selectedBlock} onChange={updateBlock} />
            ) : (
              <div className="text-center text-slate py-12">
                <Type size={28} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">Select a block to edit its properties</p>
              </div>
            )}
          </div>

          {/* Token picker — always visible at bottom of right panel */}
          <TokenPicker />
        </div>
      </div>

      {/* Preview Modal */}
      {previewHtml && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewHtml(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
              <h2 className="font-bold">Email Preview</h2>
              <button onClick={() => setPreviewHtml(null)} className="text-slate hover:text-ink text-sm font-semibold">Close</button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              <iframe
                srcDoc={previewHtml}
                className="w-full bg-white mx-auto rounded-lg shadow-sm border border-black/[0.06]"
                style={{ maxWidth: 640, height: 600 }}
                title="Email preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
